import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Sezione Casella IMAP dedicata per-agenzia (config + test login + polling manuale).
 * Estratta da AgencySettings.jsx per essere riutilizzabile all'interno della pagina
 * "Impostazioni Email" (scheda "IMAP / Ricezione").
 */
export function AgencyInboxSettingsSection({ variant = "boxed" }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [state, setState] = useState({
    enabled: false, imap_host: "imap.gmail.com", imap_port: 993, imap_user: "",
    imap_pass: "", imap_folder: "INBOX", only_replies_to_sent: true,
    imap_pass_set: false, last_polled_at: null, last_polled_count: 0, last_error: null,
  });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency/inbox-settings");
      setState((s) => ({ ...s, ...data, imap_pass: "" }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!state.enabled,
        imap_host: state.imap_host,
        imap_port: Number(state.imap_port) || 993,
        imap_user: state.imap_user,
        imap_folder: state.imap_folder || "INBOX",
        only_replies_to_sent: !!state.only_replies_to_sent,
      };
      if (state.imap_pass && state.imap_pass.trim()) payload.imap_pass = state.imap_pass;
      const { data } = await api.put("/agency/inbox-settings", payload);
      setState((s) => ({ ...s, ...data, imap_pass: "" }));
      toast.success("Casella IMAP salvata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const testLogin = async () => {
    setTesting(true);
    try {
      const payload = {};
      if (state.imap_pass && state.imap_pass.trim()) payload.imap_pass = state.imap_pass;
      if (state.imap_host) payload.imap_host = state.imap_host;
      if (state.imap_port) payload.imap_port = Number(state.imap_port);
      if (state.imap_user) payload.imap_user = state.imap_user;
      if (state.imap_folder) payload.imap_folder = state.imap_folder;
      const { data } = await api.post("/agency/inbox-settings/test", payload);
      if (data?.ok) {
        toast.success(`✅ Login OK — ${data.folder}: ${data.mailbox_count} messaggi`);
        setState((s) => ({ ...s, last_polled_at: new Date().toISOString(), last_error: null }));
      } else {
        toast.error(`❌ ${data?.error || "Login fallito"}`);
        setState((s) => ({ ...s, last_error: data?.error || "Login fallito" }));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };
  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/agency/inbox-settings/poll-now");
      toast.success(`📥 Polling: ${data.saved} nuove email salvate (di ${data.fetched} scaricate)`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPolling(false); }
  };

  if (loading) return <div className="text-slate-500 text-sm py-8">Caricamento casella IMAP...</div>;

  const wrapperClass = variant === "flat"
    ? "" // usato dentro un TabContent già stilizzato
    : "mt-6 border-t pt-6";

  return (
    <div className={wrapperClass} data-testid="agency-inbox-settings-section">
      {variant === "boxed" && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="font-display text-xl font-semibold">📥 Casella IMAP Agenzia</span>
          <span className="text-xs text-slate-500">Ricezione email di questa agenzia</span>
        </div>
      )}
      {/* Badge stato connessione IMAP */}
      {state.enabled && state.imap_pass_set && (
        <div className="mb-3">
          {state.last_polled_at && !state.last_error ? (
            <span
              className="text-xs text-emerald-800 inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 border border-emerald-300 font-semibold"
              data-testid="imap-verified-badge"
              title={`Ultimo polling riuscito: ${new Date(state.last_polled_at).toLocaleString("it-IT")}`}
            >
              ✅ IMAP connesso
              <span className="font-normal text-emerald-700 text-[10px] ml-1">
                · {new Date(state.last_polled_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            </span>
          ) : state.last_error ? (
            <span className="text-xs text-rose-800 inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-100 border border-rose-300 font-semibold" data-testid="imap-error-badge" title={state.last_error}>
              ❌ IMAP errore
            </span>
          ) : (
            <span className="text-xs text-amber-700 inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-50 border border-amber-300" data-testid="imap-untested-badge">
              ⚠ IMAP attivo — non ancora testato
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2 flex items-center gap-2 p-2 rounded bg-slate-50 border border-slate-200">
          <input type="checkbox" id="ag-imap-enabled" checked={!!state.enabled}
                 onChange={(e) => setState({ ...state, enabled: e.target.checked })}
                 data-testid="agency-imap-enabled" />
          <label htmlFor="ag-imap-enabled" className="text-sm font-medium">Attiva casella IMAP dedicata per questa agenzia</label>
        </div>
        {/* Provider preset picker IMAP */}
        <div className="sm:col-span-2">
          <Label>🌐 Provider IMAP (preset host + porta)</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {[
              { key: "gmail", label: "Gmail", host: "imap.gmail.com", port: 993 },
              { key: "yahoo", label: "Yahoo", host: "imap.mail.yahoo.com", port: 993 },
              { key: "outlook", label: "Outlook / 365", host: "outlook.office365.com", port: 993 },
              { key: "libero", label: "Libero", host: "imapmail.libero.it", port: 993 },
              { key: "aruba", label: "Aruba", host: "imaps.aruba.it", port: 993 },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`agency-imap-preset-${p.key}`}
                onClick={() => setState({ ...state, imap_host: p.host, imap_port: p.port })}
                className={`text-xs px-2.5 py-1 rounded border transition ${
                  (state.imap_host || "") === p.host
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Server IMAP (host)</Label>
          <Input value={state.imap_host || ""} onChange={(e) => setState({ ...state, imap_host: e.target.value })}
                 placeholder="imap.mail.yahoo.com" data-testid="agency-imap-host" />
        </div>
        <div>
          <Label>Porta</Label>
          <Input type="number" min={1} max={65535} value={state.imap_port || 993}
                 onChange={(e) => setState({ ...state, imap_port: e.target.value })}
                 data-testid="agency-imap-port" />
        </div>
        <div>
          <Label>Indirizzo email (user)</Label>
          <Input type="email" value={state.imap_user || ""}
                 onChange={(e) => setState({ ...state, imap_user: e.target.value })}
                 placeholder="agenzia@dominio.it" data-testid="agency-imap-user" />
        </div>
        <div>
          <Label>
            Password IMAP{" "}
            {state.imap_pass_set ? (
              <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold">🔒 salvata</span>
            ) : (
              <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-semibold">⚠ mancante</span>
            )}
          </Label>
          <div className="relative">
            <Input
              type="password"
              value={state.imap_pass || ""}
              onChange={(e) => setState({ ...state, imap_pass: e.target.value })}
              placeholder={state.imap_pass_set ? "•••••••••••• (già salvata — digita solo per sostituire)" : "App password / password IMAP"}
              data-testid="agency-imap-pass"
              autoComplete="new-password"
            />
            {state.imap_pass_set && !state.imap_pass && (
              <div className="mt-1 flex items-center justify-between text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                <span>✓ Password crittografata e salvata nel database (Fernet AES-128)</span>
                <button
                  type="button"
                  className="text-blue-600 hover:underline font-semibold"
                  onClick={() => {
                    if (window.confirm("Vuoi rimuovere la password IMAP salvata? Dovrai reinserirla per riabilitare il polling.")) {
                      setState({ ...state, imap_pass: "", imap_pass_set: false });
                      (async () => {
                        try {
                          await api.delete("/agency/inbox-settings/pass");
                          toast.success("Password IMAP rimossa");
                          await load();
                        } catch (e) { toast.error(formatApiError(e)); }
                      })();
                    }
                  }}
                  data-testid="agency-imap-pass-clear"
                >
                  🗑 Rimuovi
                </button>
              </div>
            )}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">Per Gmail usa una <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 underline">App Password Gmail</a> · Per Yahoo genera una <a href="https://login.yahoo.com/account/security/app-passwords" target="_blank" rel="noreferrer" className="text-blue-600 underline">Password per l&apos;app Yahoo</a> (16 caratteri, servono 2FA). La password viene cifrata prima di essere salvata.</p>
        </div>
        <div>
          <Label>Cartella IMAP</Label>
          <Input value={state.imap_folder || "INBOX"}
                 onChange={(e) => setState({ ...state, imap_folder: e.target.value })}
                 placeholder="INBOX" data-testid="agency-imap-folder" />
        </div>
        <div className="sm:col-span-2 flex items-center gap-2 p-2 rounded bg-amber-50 border border-amber-200">
          <input type="checkbox" id="ag-imap-only-replies" checked={!!state.only_replies_to_sent}
                 onChange={(e) => setState({ ...state, only_replies_to_sent: e.target.checked })}
                 data-testid="agency-imap-only-replies" />
          <label htmlFor="ag-imap-only-replies" className="text-sm">🔒 Accetta solo risposte a email inviate dal CRM (raccomandato per bloccare spam)</label>
        </div>
      </div>

      {(state.last_polled_at || state.last_error) && (
        <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2" data-testid="agency-imap-status">
          <div>⏱ Ultimo polling: <strong>{state.last_polled_at ? new Date(state.last_polled_at).toLocaleString("it-IT") : "mai"}</strong></div>
          <div>📥 Ultime email salvate: <strong>{state.last_polled_count || 0}</strong></div>
          {state.last_error && <div className="text-rose-700">❌ Ultimo errore: {state.last_error}</div>}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} data-testid="agency-imap-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva impostazioni"}
        </Button>
        <Button variant="outline" onClick={testLogin} disabled={testing} data-testid="agency-imap-test">
          🧪 {testing ? "Test in corso..." : "Test login IMAP"}
        </Button>
        {state.enabled && state.imap_pass_set && (
          <Button variant="outline" onClick={pollNow} disabled={polling} data-testid="agency-imap-poll-now">
            📥 {polling ? "Polling in corso..." : "Polling adesso"}
          </Button>
        )}
      </div>
    </div>
  );
}
